import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "stimulus-use"

export default class extends Controller {
  static targets = ["input", "hiddenInput", "optionsList", "option", "chipContainer", "toggleButton"]

  connect() {
    useClickOutside(this, { element: this.element })
    this.highlightedIndex = -1
    this.updateHiddenInputs()
    this.updateChips()
  }

  disconnect() {
    if (this.hasToggleButtonText) {
      this.toggleButtonTarget.textContent = this.toggleButtonTextValue
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.optionsListTarget.classList.toggle("hidden")
    if (!this.optionsListTarget.classList.contains("hidden")) {
      this.inputTarget.focus()
    }
  }

  clickOutside(event) {
    this.close()
  }

  close() {
    this.optionsListTarget.classList.add("hidden")
    this.unhighlightAll()
    this.highlightedIndex = -1
  }

  filter() {
    const query = this.inputTarget.value.toLowerCase()
    this.optionTargets.forEach(option => {
      const text = option.querySelector(".option-label").innerText.toLowerCase()
      option.classList.toggle("hidden", !text.includes(query))
    })
    this.optionsListTarget.classList.remove("hidden")
  }

  select(event) {
    event.stopPropagation()
    const selectedOption = event.currentTarget
    const userId = selectedOption.dataset.userId
    const checkIcon = selectedOption.querySelector(".check-icon")

    if (checkIcon) {
      checkIcon.classList.toggle("hidden")
      selectedOption.classList.toggle("bg-blue-50")
    }

    this.updateHiddenInputs()
    this.updateChips()
    this.updateToggleText()
  }

  removeChip(event, userId) {
    event.preventDefault()
    event.stopPropagation()
    const optionRow = this.element.querySelector(`[data-user-id="${userId}"][data-employee-select-target="option"]`)
    if (optionRow) {
      const checkIcon = optionRow.querySelector(".check-icon")
      if (checkIcon) checkIcon.classList.add("hidden")
      optionRow.classList.remove("bg-blue-50")
    }
    this.updateHiddenInputs()
    this.updateChips()
    this.updateToggleText()
  }

  updateHiddenInputs() {
    const form = this.element.closest("form")
    if (!form) return

    form.querySelectorAll("input[name='user_ids[]']").forEach(input => input.remove())

    const selectedOptions = this.element.querySelectorAll("[data-employee-select-target='option'].bg-blue-50")
    selectedOptions.forEach(option => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "user_ids[]"
      input.value = option.dataset.userId
      form.appendChild(input)
    })
  }

  updateChips() {
    const container = this.chipContainer
    if (!container) return
    container.innerHTML = ""

    const selectedOptions = this.element.querySelectorAll("[data-employee-select-target='option'].bg-blue-50")
    selectedOptions.forEach(option => {
      const name = option.dataset.userName
      const userId = option.dataset.userId
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200"

      const label = document.createElement("span")
      label.textContent = name
      chip.appendChild(label)

      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:text-blue-700 hover:bg-blue-200 focus:outline-none shrink-0"
      btn.setAttribute("aria-label", `Retirer ${name}`)
      btn.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`
      btn.addEventListener("click", (e) => this.removeChip(e, userId))
      chip.appendChild(btn)

      container.appendChild(chip)
    })
  }

  get chipContainer() {
    const parent = this.element.parentElement
    return parent ? parent.querySelector("[data-employee-select-target='chipContainer']") : null
  }

  updateToggleText() {
    if (!this.hasToggleButtonTarget || !this.hasToggleButtonText) return
    const count = this.element.querySelectorAll("[data-employee-select-target='option'].bg-blue-50").length
    if (count === 0) {
      this.toggleButtonTarget.textContent = this.toggleButtonTextValue
    } else {
      this.toggleButtonTarget.textContent = `${count} sélectionné${count > 1 ? "s" : ""}`
    }
  }

  navigate(event) {
    if (this.optionsListTarget.classList.contains("hidden")) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        this.optionsListTarget.classList.remove("hidden")
      }
      return
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightNext()
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightPrevious()
        break
      case "Enter":
        event.preventDefault()
        if (this.highlightedIndex >= 0) {
          this.visibleOptionTargets[this.highlightedIndex].click()
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  highlightNext() {
    this.unhighlightAll()
    this.highlightedIndex++
    if (this.highlightedIndex >= this.visibleOptionTargets.length) {
      this.highlightedIndex = 0
    }
    this.highlight(this.highlightedIndex)
  }

  highlightPrevious() {
    this.unhighlightAll()
    this.highlightedIndex--
    if (this.highlightedIndex < 0) {
      this.highlightedIndex = this.visibleOptionTargets.length - 1
    }
    this.highlight(this.highlightedIndex)
  }

  highlight(index) {
    const option = this.visibleOptionTargets[index]
    if (option) {
      option.classList.add("ring-2", "ring-blue-500")
      option.scrollIntoView({ block: "nearest" })
    }
  }

  unhighlightAll() {
    this.optionTargets.forEach(option => {
      option.classList.remove("ring-2", "ring-blue-500")
    })
  }

  get visibleOptionTargets() {
    return this.optionTargets.filter(target => !target.classList.contains("hidden"))
  }
}
